'use client';

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import {
  createBusinessRevenueSourceDefinition,
  deactivateBusinessRevenueSourceDefinition,
  decideBusinessRevenueSourceDefinition,
  getBusinessReportCatalog,
  getBusinessReportSources,
  getBusinessRevenueSourceDefinitions,
  getBusinessRevenueSources,
  submitBusinessRevenueSourceDefinition,
  updateBusinessRevenueSourceDefinition,
  type BusinessReportCatalogItem,
  type BusinessReportSource,
  type BusinessRevenueSource,
  type BusinessRevenueSourceDefinition,
  type BusinessRevenueSourceDefinitionInput,
} from '../lib/api/business-reports';
import { AdminAlertModal, Loader } from './common';

const EMPTY: BusinessRevenueSourceDefinitionInput = {
  definitionKey: '',
  definitionName: '',
  description: null,
  dataSourceKey: null,
  revenueSourceKey: '',
  sourceOwner: '',
  financeOwner: 'Finance team',
  deliveryMethod: 'SYSTEM_GENERATED_FILE',
  refreshCadence: 'DAILY',
  businessDateField: '',
  amountField: '',
  currency: 'INR',
  signPolicy: 'PRESERVE_SIGNED_ONCE',
  ledgerDestination: 'BUSINESS_DAILY_REVENUE_FACT',
  contributionTreatment: 'SEPARATE_AUTOMATIC_SOURCE',
  sourceContract: { stableReferenceField: '', centerField: '', correctionPolicy: 'ADDITIVE_ONLY' },
  reportKeys: [],
  validationEvidenceReference: null,
  financeApprovalReference: null,
  reconciliationEvidenceReferences: [],
  minimumReconciliations: 1,
  effectiveFrom: null,
  effectiveTo: null,
};

export function BusinessRevenueSourceDefinitionsTab({ active, token, onTopBarActionsChange }: { active: boolean; token?: string; onTopBarActionsChange?: (actions: ReactNode) => void }) {
  const [rows, setRows] = useState<BusinessRevenueSourceDefinition[]>([]);
  const [dataSources, setDataSources] = useState<BusinessReportSource[]>([]);
  const [revenueSources, setRevenueSources] = useState<BusinessRevenueSource[]>([]);
  const [reports, setReports] = useState<BusinessReportCatalogItem[]>([]);
  const [form, setForm] = useState<BusinessRevenueSourceDefinitionInput>(EMPTY);
  const [contractText, setContractText] = useState(JSON.stringify(EMPTY.sourceContract, null, 2));
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [decision, setDecision] = useState<{ key: string; action: 'approve' | 'reject' } | null>(null);
  const [decisionNotes, setDecisionNotes] = useState('');
  const [busy, setBusy] = useState('');
  const [alert, setAlert] = useState<{ type: 'success' | 'error' | 'info' | 'warning'; title: string; message: string } | null>(null);

  const load = useCallback(async () => {
    setBusy('Loading source-onboarding governance...');
    try {
      const [definitions, technical, revenue, catalog] = await Promise.all([
        getBusinessRevenueSourceDefinitions(token),
        getBusinessReportSources(token),
        getBusinessRevenueSources(token),
        getBusinessReportCatalog(token),
      ]);
      setRows(definitions);
      setDataSources(technical);
      setRevenueSources(revenue);
      setReports(catalog);
    } catch (error) {
      setAlert({ type: 'error', title: 'Could not load source definitions', message: errorMessage(error) });
    } finally {
      setBusy('');
    }
  }, [token]);

  useEffect(() => { if (active) void load(); }, [active, load]);

  const selectedRevenue = useMemo(
    () => revenueSources.find((source) => source.sourceKey === form.revenueSourceKey) ?? null,
    [form.revenueSourceKey, revenueSources],
  );

  useEffect(() => {
    if (!active || !onTopBarActionsChange) return undefined;
    onTopBarActionsChange(
      <div
        className="business-calendar-top-status"
        title="Only validated and approved source definitions can be activated."
      >
        <span>Activation safeguard</span>
        <strong className="business-source-status active">ENFORCED</strong>
      </div>,
    );
    return () => onTopBarActionsChange(null);
  }, [active, onTopBarActionsChange]);

  if (!active) return null;

  const reset = () => {
    setEditingKey(null);
    setForm(EMPTY);
    setContractText(JSON.stringify(EMPTY.sourceContract, null, 2));
  };

  const chooseRevenue = (sourceKey: string) => {
    const source = revenueSources.find((item) => item.sourceKey === sourceKey);
    const manual = source?.sourceMode === 'MANUAL';
    setForm({
      ...form,
      revenueSourceKey: sourceKey,
      dataSourceKey: manual ? null : form.dataSourceKey,
      ledgerDestination: manual ? 'BUSINESS_MANUAL_REVENUE_ADJUSTMENT' : 'BUSINESS_DAILY_REVENUE_FACT',
      contributionTreatment: manual ? 'SEPARATE_MANUAL_ADJUSTMENT' : 'SEPARATE_AUTOMATIC_SOURCE',
    });
  };

  const save = async () => {
    let sourceContract: Record<string, unknown>;
    try {
      const parsed = JSON.parse(contractText);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) throw new Error();
      sourceContract = parsed;
    } catch {
      setAlert({ type: 'error', title: 'Source contract is invalid', message: 'Enter a valid non-empty JSON object.' });
      return;
    }
    setBusy(editingKey ? 'Saving definition as draft...' : 'Creating definition draft...');
    try {
      const input = { ...form, sourceContract };
      if (editingKey) await updateBusinessRevenueSourceDefinition(editingKey, input, token);
      else await createBusinessRevenueSourceDefinition(input, token);
      reset();
      await load();
      setAlert({ type: 'success', title: 'Definition draft saved', message: 'Submit it only after validation, Finance and reconciliation references are complete.' });
    } catch (error) {
      setAlert({ type: 'error', title: 'Definition was not saved', message: errorMessage(error) });
    } finally {
      setBusy('');
    }
  };

  const submit = async (key: string) => {
    setBusy('Submitting definition for separate approval...');
    try { await submitBusinessRevenueSourceDefinition(key, token); await load(); }
    catch (error) { setAlert({ type: 'error', title: 'Submission blocked', message: errorMessage(error) }); }
    finally { setBusy(''); }
  };

  const decide = async () => {
    if (!decision) return;
    setBusy(`${decision.action === 'approve' ? 'Approving' : 'Rejecting'} definition...`);
    try {
      await decideBusinessRevenueSourceDefinition(decision.key, decision.action, decisionNotes, token);
      setDecision(null); setDecisionNotes(''); await load();
    } catch (error) { setAlert({ type: 'error', title: 'Decision failed', message: errorMessage(error) }); }
    finally { setBusy(''); }
  };

  const edit = (row: BusinessRevenueSourceDefinition) => {
    setEditingKey(row.definitionKey);
    setForm({
      definitionKey: row.definitionKey, definitionName: row.definitionName, description: row.description,
      dataSourceKey: row.dataSource?.sourceKey ?? null, revenueSourceKey: row.revenueSource.sourceKey,
      sourceOwner: row.sourceOwner, financeOwner: row.financeOwner, deliveryMethod: row.deliveryMethod,
      refreshCadence: row.refreshCadence, businessDateField: row.businessDateField, amountField: row.amountField,
      currency: row.currency, signPolicy: row.signPolicy, ledgerDestination: row.ledgerDestination,
      contributionTreatment: row.contributionTreatment, sourceContract: row.sourceContract,
      reportKeys: row.reportKeys, validationEvidenceReference: row.validationEvidenceReference,
      financeApprovalReference: row.financeApprovalReference,
      reconciliationEvidenceReferences: row.reconciliationEvidenceReferences,
      minimumReconciliations: row.minimumReconciliations, effectiveFrom: row.effectiveFrom, effectiveTo: row.effectiveTo,
    });
    setContractText(JSON.stringify(row.sourceContract, null, 2));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const evidenceCount = form.reconciliationEvidenceReferences.filter(Boolean).length;
  return (
    <section className="tab-content business-source-page" id="tab-business-source-definitions">
      {busy && <Loader message={busy} />}
      <div className="business-file-import-policy">
        <strong>Controlled ledger rule</strong>
        <p>Automatic sources can write only immutable Daily Revenue facts. Manual sources can write only separately labelled maker-checker adjustments. Neither path can overwrite the other.</p>
      </div>

      <div className="enterprise-card business-source-form-card business-definition-card">
        <div className="enterprise-card-heading business-definition-heading"><div><strong>{editingKey ? `Edit ${editingKey}` : 'Create source definition'}</strong><span>Approved definitions remain active. Creating or editing a definition produces a new inactive draft for separate checker approval.</span></div><span className="business-definition-draft-badge governed">Governed changes</span></div>
        <div className="business-definition-section-heading"><span>01</span><div><h3>Definition, mapping and governance</h3><p>Identify the source, accountable owners, financial fields and effective approval period.</p></div></div>
        <div className="business-source-form-grid business-definition-primary-grid">
          <Field label="Definition key"><input disabled={Boolean(editingKey)} value={form.definitionKey} onChange={(e) => setForm({ ...form, definitionKey: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '') })} /></Field>
          <Field label="Definition name"><input value={form.definitionName} onChange={(e) => setForm({ ...form, definitionName: e.target.value })} /></Field>
          <Field label="Revenue source"><select value={form.revenueSourceKey} onChange={(e) => chooseRevenue(e.target.value)}><option value="">Select</option>{revenueSources.map((source) => <option key={source.sourceKey} value={source.sourceKey}>{source.sourceName} · {source.sourceMode}</option>)}</select></Field>
          <Field label="Technical source"><select disabled={selectedRevenue?.sourceMode === 'MANUAL'} value={form.dataSourceKey || ''} onChange={(e) => setForm({ ...form, dataSourceKey: e.target.value || null })}><option value="">Select</option>{dataSources.map((source) => <option key={source.sourceKey} value={source.sourceKey}>{source.sourceName} · {source.validationStatus}</option>)}</select></Field>
          <Field label="Source owner"><input value={form.sourceOwner} onChange={(e) => setForm({ ...form, sourceOwner: e.target.value })} /></Field>
          <Field label="Finance owner"><input value={form.financeOwner} onChange={(e) => setForm({ ...form, financeOwner: e.target.value })} /></Field>
          <Field label="Delivery method"><input value={form.deliveryMethod} onChange={(e) => setForm({ ...form, deliveryMethod: e.target.value })} /></Field>
          <Field label="Refresh cadence"><input value={form.refreshCadence} onChange={(e) => setForm({ ...form, refreshCadence: e.target.value })} /></Field>
          <Field label="Business-date field"><input value={form.businessDateField} onChange={(e) => setForm({ ...form, businessDateField: e.target.value })} /></Field>
          <Field label="Reportable amount field"><input value={form.amountField} onChange={(e) => setForm({ ...form, amountField: e.target.value })} /></Field>
          <Field label="Currency"><input maxLength={3} value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })} /></Field>
          <Field label="Sign/correction policy"><input value={form.signPolicy} onChange={(e) => setForm({ ...form, signPolicy: e.target.value })} /></Field>
          <Field label="Minimum reconciliations"><input type="number" min={1} max={10} value={form.minimumReconciliations} onChange={(e) => setForm({ ...form, minimumReconciliations: Number(e.target.value) })} /></Field>
          <Field label="Effective from"><input type="date" value={form.effectiveFrom || ''} onChange={(e) => setForm({ ...form, effectiveFrom: e.target.value || null })} /></Field>
          <Field label="Effective to"><input type="date" value={form.effectiveTo || ''} onChange={(e) => setForm({ ...form, effectiveTo: e.target.value || null })} /></Field>
          <Field label="Validation evidence"><input value={form.validationEvidenceReference || ''} onChange={(e) => setForm({ ...form, validationEvidenceReference: e.target.value || null })} /></Field>
          <Field label="Finance approval reference"><input value={form.financeApprovalReference || ''} onChange={(e) => setForm({ ...form, financeApprovalReference: e.target.value || null })} /></Field>
        </div>
        <div className="business-definition-section-heading"><span>02</span><div><h3>Evidence and report use</h3><p>Record reconciliation evidence, restricted source metadata and permitted report destinations.</p></div></div>
        <div className="business-source-form-grid business-definition-evidence-grid">
          <Field label={`Reconciliation references (${evidenceCount}/${form.minimumReconciliations})`} wide><textarea rows={4} value={form.reconciliationEvidenceReferences.join('\n')} onChange={(e) => setForm({ ...form, reconciliationEvidenceReferences: e.target.value.split(/\r?\n/).map((value) => value.trim()).filter(Boolean) })} placeholder="One controlled evidence reference per line" /></Field>
          <Field label="Source contract (restricted metadata only)" wide><textarea rows={7} value={contractText} onChange={(e) => setContractText(e.target.value)} spellCheck={false} /></Field>
          <Field label="Reports" wide><div className="business-source-checkbox-grid">{reports.map((report) => <label key={report.reportKey}><input type="checkbox" checked={form.reportKeys.includes(report.reportKey)} onChange={(e) => setForm({ ...form, reportKeys: e.target.checked ? [...form.reportKeys, report.reportKey] : form.reportKeys.filter((key) => key !== report.reportKey) })} /> {report.reportName}{report.isActive ? '' : ' · inactive'}</label>)}</div></Field>
        </div>
        <div className="business-source-form-actions"><span>Definitions remain inactive until submitted and approved by a separate checker.</span><div>{editingKey && <button className="btn btn-outline" onClick={reset}>Cancel</button>}<button className="btn btn-primary" disabled={!form.definitionKey || !form.definitionName || !form.revenueSourceKey || !form.reportKeys.length} onClick={() => void save()}>{editingKey ? 'Save as draft' : 'Create draft'}</button></div></div>
      </div>

      <div className="enterprise-card"><div className="enterprise-table-wrap"><table className="business-source-table"><thead><tr><th>Definition</th><th>Contribution</th><th>Evidence readiness</th><th>Governance</th><th>Actions</th></tr></thead><tbody>
        {rows.map((row) => <tr key={row.definitionKey}><td><strong>{row.definitionName}</strong><br /><span className="business-source-secondary">{row.definitionKey} · {row.dataSource?.sourceName || 'No technical connector'}</span></td><td><strong>{row.revenueSource.sourceName}</strong><br /><span className="business-source-secondary">{row.ledgerDestination === 'BUSINESS_DAILY_REVENUE_FACT' ? 'Immutable automatic facts' : 'Separate manual adjustments'} · {row.amountField}</span></td><td><Readiness row={row} /></td><td><span className={`business-source-status ${row.isActive ? 'active' : row.approvalStatus === 'PENDING_APPROVAL' ? 'warning' : 'invalid'}`}>{row.approvalStatus}</span><br /><span className="business-source-secondary">Maker {row.submittedBy || '—'} · Checker {row.approvedBy || '—'}</span></td><td><div className="business-source-row-actions"><button className="btn btn-outline btn-sm" onClick={() => edit(row)}>Edit</button>{row.approvalStatus === 'DRAFT' && <button className="btn btn-primary btn-sm" onClick={() => void submit(row.definitionKey)}>Submit</button>}{row.approvalStatus === 'PENDING_APPROVAL' && <><button className="btn btn-primary btn-sm" onClick={() => setDecision({ key: row.definitionKey, action: 'approve' })}>Approve</button><button className="btn btn-outline btn-sm" onClick={() => setDecision({ key: row.definitionKey, action: 'reject' })}>Reject</button></>}{row.isActive && <button className="btn btn-outline btn-sm" onClick={() => void deactivateBusinessRevenueSourceDefinition(row.definitionKey, token).then(load).catch((error) => setAlert({ type: 'error', title: 'Deactivation failed', message: errorMessage(error) }))}>Deactivate</button>}</div></td></tr>)}
        {!rows.length && <tr><td colSpan={5}>No source definition exists. No new source can activate.</td></tr>}
      </tbody></table></div></div>

      {decision && <div className="business-source-modal-overlay" role="dialog" aria-modal="true"><div className="business-file-import-modal"><div className="business-source-modal-header"><h3>{decision.action === 'approve' ? 'Approve source definition' : 'Reject source definition'}</h3><p>The checker must be different from the maker. Approval unlocks eligibility only; sources and reports remain separately inactive.</p></div><div className="business-file-import-modal-body"><Field label={decision.action === 'reject' ? 'Rejection reason' : 'Approval notes'}><textarea rows={4} value={decisionNotes} onChange={(e) => setDecisionNotes(e.target.value)} /></Field></div><div className="business-source-modal-footer"><button className="btn btn-outline" onClick={() => setDecision(null)}>Cancel</button><button className="btn btn-primary" disabled={decision.action === 'reject' && !decisionNotes.trim()} onClick={() => void decide()}>{decision.action === 'approve' ? 'Approve definition' : 'Reject definition'}</button></div></div></div>}
      {alert && <AdminAlertModal {...alert} onClose={() => setAlert(null)} />}
    </section>
  );
}

function Field({ label, children, wide = false }: { label: string; children: React.ReactNode; wide?: boolean }) { return <label className={`business-source-field${wide ? ' wide' : ''}`}><span>{label}</span>{children}</label>; }
function Readiness({ row }: { row: BusinessRevenueSourceDefinition }) { const ready = Object.values(row.readiness).filter(Boolean).length; const total = Object.keys(row.readiness).length; return <><strong>{ready}/{total} gates</strong><br /><span className="business-source-secondary">Finance {row.readiness.financeApprovalReady ? 'ready' : 'pending'} · Reconciliation {row.readiness.reconciliationReady ? 'ready' : 'pending'} · Report {row.readiness.reportMappingReady ? 'mapped' : 'missing'}</span></>; }
function errorMessage(error: unknown) { return error instanceof Error ? error.message : 'An unexpected error occurred.'; }
